import { calculateVariant } from '@pst/shared';
import type { Offer as OfferView } from '../../api';
import type { Cell, Sheet } from '../../export/sheet';

/**
 * The offer as Excel sheets: per variant the calculation (positions and
 * extras with hours per role, own and third-party share, price), the sums,
 * and a sheet with the offer's details. Figures as numbers; what the price
 * sheet shows, calculated exactly as the interface does.
 */
export function offerSheets(offer: OfferView): Sheet[] {
  const sheets: Sheet[] = [];
  sheets.push({
    name: 'Angebot',
    title: `Angebot ${offer.code} – ${offer.name}`,
    subtitle: `Stand ${new Date().toLocaleDateString('de-DE')}`,
    columns: [{ label: 'Angabe', width: 26 }, { label: 'Wert', width: 70 }],
    rows: [
      ['Kürzel', offer.code], ['Bezeichnung', offer.name], ['Auftraggebende', offer.client], ['Los', offer.lot], ['Ort', offer.location],
      ['Bundesland', offer.state], ['Status', offer.status], ['Abgabe', offer.dueDate ?? ''], ['Angebotsdatum', offer.offerDate ?? ''],
      ['Bindefrist (Tage)', offer.bindingDays ?? ''], ['Beschreibung', offer.description], ['Varianten', offer.variants.map((v) => `${v.code} ${v.name}`).join(' · ')],
    ],
  });
  for (const v of offer.variants) {
    const d = v.data;
    const r = calculateVariant(d);
    const roles = d.roles.map((role) => role.short);
    const columns = [
      { label: 'Pos.', width: 7 }, { label: 'Leistung', width: 44 }, { label: 'Art', width: 12 },
      ...roles.map((s) => ({ label: `Std. ${s}`, format: 'quantity' as const })),
      { label: 'Eigenleistung €', format: 'amount' as const }, { label: 'Fremdleistung €', format: 'amount' as const }, { label: 'Preis netto €', format: 'amount' as const },
    ];
    const rows: Cell[][] = [];
    const bold: number[] = [];
    d.positions.forEach((p, i) => {
      const l = r.positions[i]!;
      rows.push([String(i + 1), p.title, p.kind, ...p.hours.map((h) => h ?? null), l.own, l.foreign, l.price]);
    });
    if (d.extras.length) {
      bold.push(rows.length);
      rows.push(['', 'Zusatzleistungen', '', ...roles.map(() => null), null, null, null]);
      d.extras.forEach((e, i) => {
        const l = r.extras[i]!;
        rows.push([`Z${i + 1}`, e.title, e.unit, ...e.hours.map((h) => h ?? null), l.own, l.foreign, l.price]);
      });
    }
    const pad = (label: string, value: number): Cell[] => ['', label, '', ...roles.map(() => null), null, null, value];
    bold.push(rows.length, rows.length + 1, rows.length + 2);
    rows.push(pad('Summe netto', r.net), pad(`Umsatzsteuer ${Math.round(d.params.vat * 100)} %`, r.vatAmount), pad('Summe brutto', r.gross));
    sheets.push({
      name: `Variante ${v.code}`,
      title: `${offer.code} · Variante ${v.code} – ${v.name}`,
      subtitle: `Eigene Stunden ${r.ownHours.toLocaleString('de-DE')} · Erlös je Stunde ${r.revenuePerHour === null ? '—' : r.revenuePerHour.toLocaleString('de-DE', { maximumFractionDigits: 2 })} € · Zusatzleistungen ohne Summe`,
      columns,
      rows,
      boldRows: bold,
    });
  }
  return sheets;
}
